import React, { useCallback, useState } from 'react';

import { useSelector } from 'react-redux';

import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';

import UserSearchWidget, { SearchResult } from '~/components/Common/UserSearchWidget';
import CustomerServiceRepSelector from '~/components/LuxLoyalty/CustomerServiceRepSelector';

import { useChangeTier } from '~/hooks/apis/loyalty/useChangeTier';
import { useSetAccountCustomerServiceRep } from '~/hooks/apis/loyalty/useCustomerServiceRepAssignments';
import { useEnrolLuxLoyaltyAccount } from '~/hooks/apis/loyalty/useEnrolLuxLoyaltyAccount';

import { getInfiniteOnboardingEmailWarning } from '~/services/LuxLoyaltyService';
import UsersService from '~/services/UsersService';

import { getLuxLoyaltyTierDisplayName } from '~/utils/luxLoyalty';

import ConfirmInfiniteEnrolmentDialog from './ConfirmInfiniteEnrolmentDialog';
import type { CreateInfiniteOutcome } from './createInfiniteAccountShared';
import {
  CONCIERGE_REP_FIELD_LABEL,
  CONCIERGE_REP_HELPER_TEXT,
  CONCIERGE_REP_REQUIRED_ERROR,
  EXISTING_CUSTOMER_SEARCH_LABEL,
} from './createInfiniteAccountShared';
import { useConciergeRepLabel } from './useConciergeRepLabel';
import { useInfiniteCustomerLoyaltyLookup } from './useInfiniteCustomerLoyaltyLookup';

interface SelectedCustomer {
  id: string;
  fullName: string;
  email: string;
}

const CUSTOMER_SEARCH_FIELD_ID = 'infinite-existing-customer-search';
const TIER_CHANGE_REMARK_SOURCE = 'ADMIN_PORTAL_MANUAL_UPGRADE';

interface Props {
  submitted: boolean;
  onSubmitted: () => void;
  onOutcome: (outcome: CreateInfiniteOutcome) => void;
  onBack: () => void;
}

export default function CreateInfiniteExistingAccountStep({ submitted, onSubmitted, onOutcome, onBack }: Props) {
  const brand = useSelector((state: App.State) => state.tenant.brand);
  const [selectedCustomer, setSelectedCustomer] = useState<SelectedCustomer | null>(null);
  const [userSearchLabel, setUserSearchLabel] = useState('');
  const [csRepId, setCsRepId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const conciergeRepLabel = useConciergeRepLabel(csRepId);

  const { currentTier, hasSocieteAccount, accountLookupFailed, accountLookupPending } =
    useInfiniteCustomerLoyaltyLookup(selectedCustomer?.id);
  const isAlreadyInfinite = currentTier === 'infinite';

  const enrolMutation = useEnrolLuxLoyaltyAccount();
  const setRepMutation = useSetAccountCustomerServiceRep();
  const changeTierMutation = useChangeTier();

  const trimmedReason = reason.trim();
  const errors = {
    customer: selectedCustomer ? null : 'Select an existing Luxury Escapes customer.',
    csRep: csRepId ? null : CONCIERGE_REP_REQUIRED_ERROR,
    reason:
      hasSocieteAccount && !isAlreadyInfinite && !trimmedReason ? 'A reason is required to upgrade the tier.' : null,
  };
  const hasErrors = Object.values(errors).some(Boolean);

  const resetStepFields = () => {
    setSelectedCustomer(null);
    setUserSearchLabel('');
    setCsRepId(null);
    setReason('');
  };

  const handleSelectResult = useCallback(
    (result: SearchResult | null) => {
      setReason('');
      if (!result || result.entityType !== 'user') {
        setSelectedCustomer(null);
        return;
      }

      const customer = {
        id: result.value,
        fullName: result.fullName?.trim() || '',
        email: result.email,
      };
      setSelectedCustomer(customer);
      setUserSearchLabel(customer.email);

      if (customer.fullName) {
        return;
      }

      void UsersService.getUser(result.value, { brand })
        .then((user) => {
          const fullName = [user.givenName, user.surname].filter(Boolean).join(' ').trim() || user.fullName?.trim();
          if (!fullName) {
            return;
          }
          setSelectedCustomer((prev) => (prev?.id === result.value ? { ...prev, fullName } : prev));
        })
        .catch(() => undefined);
    },
    [brand],
  );

  const handleSearchInputChange = useCallback(
    (value: string) => {
      setUserSearchLabel(value);
      if (selectedCustomer && value.trim() !== selectedCustomer.email.trim()) {
        setSelectedCustomer(null);
        setReason('');
      }
    },
    [selectedCustomer],
  );

  const handleSearchChange = useCallback((_fieldId: string, value: string) => {
    if (!value) {
      setSelectedCustomer(null);
      setReason('');
    }
  }, []);

  const resolveCustomerFullName = useCallback(
    async (customer: SelectedCustomer): Promise<SelectedCustomer> => {
      if (customer.fullName) {
        return customer;
      }

      const user = await UsersService.getUser(customer.id, { brand });
      const fullName = [user.givenName, user.surname].filter(Boolean).join(' ').trim() || user.fullName?.trim() || '';
      return { ...customer, fullName };
    },
    [brand],
  );

  const handleSubmitRequest = async () => {
    if (isSubmitting) {
      return;
    }
    onSubmitted();
    if (!selectedCustomer || hasErrors || isAlreadyInfinite || accountLookupPending || accountLookupFailed) {
      return;
    }

    try {
      const customer = await resolveCustomerFullName(selectedCustomer);
      if (customer.fullName !== selectedCustomer.fullName) {
        setSelectedCustomer(customer);
      }
      setConfirmDialogOpen(true);
    } catch {
      onOutcome({
        kind: 'error',
        message:
          'Could not load the selected customer details. Try again or finish enrolment from the user detail page.',
      });
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) {
      return;
    }
    if (
      !selectedCustomer ||
      hasErrors ||
      isAlreadyInfinite ||
      accountLookupPending ||
      accountLookupFailed ||
      !csRepId
    ) {
      return;
    }

    setIsSubmitting(true);

    let customer: SelectedCustomer;
    try {
      customer = await resolveCustomerFullName(selectedCustomer);
      if (customer.fullName !== selectedCustomer.fullName) {
        setSelectedCustomer(customer);
      }
    } catch {
      onOutcome({
        kind: 'error',
        message:
          'Could not load the selected customer details. Try again or finish enrolment from the user detail page.',
      });
      setIsSubmitting(false);
      setConfirmDialogOpen(false);
      return;
    }

    const { id: customerId, fullName: customerName, email: customerEmail } = customer;

    try {
      if (!hasSocieteAccount) {
        const enrolResponse = await enrolMutation.mutateAsync({
          customerId,
          tier: 'infinite',
          customerServiceRepId: csRepId,
        });
        const onboardingEmailWarning = getInfiniteOnboardingEmailWarning(enrolResponse);
        if (onboardingEmailWarning) {
          onOutcome({
            kind: 'email-warning',
            customerName,
            customerId,
            message: onboardingEmailWarning,
          });
          resetStepFields();
          setConfirmDialogOpen(false);
          return;
        }
        onOutcome({
          kind: 'success',
          customerName,
          customerId,
          message: `${customerName} has been upgraded to Infinite.`,
        });
        resetStepFields();
        setConfirmDialogOpen(false);
        return;
      }

      const changeTierResponse = await changeTierMutation.mutateAsync({
        customerId,
        targetTier: 'infinite',
        reason: trimmedReason,
        remarkSource: TIER_CHANGE_REMARK_SOURCE,
      });

      try {
        await setRepMutation.mutateAsync({ customerId, customerServiceRepId: csRepId });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unknown error.';
        onOutcome({
          kind: 'partial',
          customerEmail,
          customerId,
          message: `Tier upgraded to Infinite but concierge representative assignment failed: ${message}. Assign the representative manually from the user detail page.`,
        });
        resetStepFields();
        setConfirmDialogOpen(false);
        return;
      }

      const onboardingEmailWarning = getInfiniteOnboardingEmailWarning(changeTierResponse);
      if (onboardingEmailWarning) {
        onOutcome({
          kind: 'email-warning',
          customerName,
          customerId,
          message: onboardingEmailWarning,
        });
        resetStepFields();
        setConfirmDialogOpen(false);
        return;
      }

      onOutcome({
        kind: 'success',
        customerName,
        customerId,
        message: `${customerName} has been upgraded to Infinite.`,
      });
      resetStepFields();
      setConfirmDialogOpen(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error.';
      onOutcome({
        kind: 'error',
        message: `Failed to upgrade customer to Infinite: ${message}`,
      });
    } finally {
      setIsSubmitting(false);
      setConfirmDialogOpen(false);
    }
  };

  const confirmMessage = (() => {
    if (!selectedCustomer) {
      return 'This will upgrade the selected customer to Infinite.';
    }
    if (!hasSocieteAccount) {
      return (
        <>
          This will enrol <strong>{selectedCustomer.fullName}</strong> into Infinite.
        </>
      );
    }
    return (
      <>
        This will upgrade <strong>{selectedCustomer.fullName}</strong> from {getLuxLoyaltyTierDisplayName(currentTier!)}{' '}
        to Infinite.
      </>
    );
  })();

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Customer *
        </Typography>
        <UserSearchWidget
          id={CUSTOMER_SEARCH_FIELD_ID}
          label={EXISTING_CUSTOMER_SEARCH_LABEL}
          formErrors={submitted && !selectedCustomer ? [CUSTOMER_SEARCH_FIELD_ID] : []}
          loading={false}
          onChange={handleSearchChange}
          onSearchInputChange={handleSearchInputChange}
          onSelectResult={handleSelectResult}
          passedAutocompleteValue={userSearchLabel}
          setPassedAutocompleteValue={setUserSearchLabel}
        />
        {submitted && !selectedCustomer && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
            {errors.customer}
          </Typography>
        )}
      </Box>

      {selectedCustomer && accountLookupPending && (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Checking Société membership…
          </Typography>
        </Stack>
      )}

      {selectedCustomer && accountLookupFailed && (
        <Alert severity="error">
          Could not load this customer&apos;s Société account. Try again or finish enrolment from the user detail page.
        </Alert>
      )}

      {selectedCustomer && !accountLookupPending && !accountLookupFailed && (
        <Alert severity={isAlreadyInfinite ? 'warning' : 'info'}>
          {isAlreadyInfinite ? (
            <>
              <strong>{selectedCustomer.fullName}</strong> is already an Infinite member. Choose a different customer or
              manage their representative from the Concierge tab.
            </>
          ) : hasSocieteAccount ? (
            <>
              <strong>{selectedCustomer.email}</strong> is currently a{' '}
              <strong>{getLuxLoyaltyTierDisplayName(currentTier!)}</strong> Société member. Submitting will upgrade them
              to Infinite and assign the selected concierge representative.
            </>
          ) : (
            <>
              <strong>{selectedCustomer.email}</strong> is not yet a Société member. Submitting will enrol them into
              Infinite and assign the selected concierge representative.
            </>
          )}
        </Alert>
      )}

      {hasSocieteAccount && !isAlreadyInfinite && (
        <TextField
          label="Reason for upgrade"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          required
          fullWidth
          multiline
          minRows={2}
          error={submitted && !!errors.reason}
          helperText={submitted ? (errors.reason ?? ' ') : 'Required when upgrading an existing Société member.'}
          disabled={isSubmitting || !selectedCustomer}
        />
      )}

      <CustomerServiceRepSelector
        value={csRepId}
        onChange={setCsRepId}
        label={CONCIERGE_REP_FIELD_LABEL}
        required
        error={submitted && !csRepId}
        helperText={submitted && !csRepId ? errors.csRep : CONCIERGE_REP_HELPER_TEXT}
        disabled={isSubmitting || isAlreadyInfinite}
      />

      <Stack direction="row" spacing={2} alignItems="center">
        <Button variant="text" onClick={onBack} disabled={isSubmitting}>
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmitRequest}
          disabled={isSubmitting || isAlreadyInfinite || accountLookupPending || accountLookupFailed}
        >
          {isSubmitting ? 'Processing…' : 'Upgrade to Infinite'}
        </Button>
      </Stack>

      <ConfirmInfiniteEnrolmentDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={handleSubmit}
        isSubmitting={isSubmitting}
        confirmLabel="Upgrade to Infinite"
        message={confirmMessage}
        conciergeRepSummary={conciergeRepLabel}
        upgradeReason={hasSocieteAccount && !isAlreadyInfinite ? trimmedReason : undefined}
      />
    </Stack>
  );
}
